import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Subject } from 'rxjs';
import { debounceTime, filter, take, takeUntil } from 'rxjs/operators';
import { LanguageService } from '@core/services/language.service';
import { ConfigConstant, RolesConstants, RoutesConstants } from '@shared/constants';
import { NavBarItem } from '@shared/models/nav-bar-item.model';
import { UserRoleService } from '@shared/services';
import { RoutesUtil } from '@shared-features/utils/routes.util';
import { UserApi } from '@shared-features/services';
import { Router } from '@angular/router';
import { CustomerServiceType, ShipmentsListType } from '@shared-features/enums/shared-enums.enum';
import { FleetType } from '@shared-features/enums/fleets.enum';
import { User } from '@features/users/models/user.model';
import { FeatureFlagService } from '@shared/services/feature-flag.service';
import { FeatureFlagConstant } from '@shared/constants/feature-flag.constant';
import { CompanyLabelType } from '@shared-features/enums/shared-enums.enum';
import { FullstoryService } from '@shared/services/fullstory.service';
import { ShipmentCreationTemplateTypes } from '@shared-features/enums';

@Component({
  selector: 'app-wrapper',
  templateUrl: './wrapper.component.html',
  styleUrls: ['./wrapper.component.scss'],
})
export class WrapperComponent implements OnInit {
  public navigationItems: NavBarItem[];
  public showOutlet = false;
  private destroy$ = new Subject();
  private currentUser: User;

  constructor(
    public languageService: LanguageService,
    private translate: TranslateService,
    private userRoleService: UserRoleService,
    private userApi: UserApi,
    private router: Router,
    private featureFlagService: FeatureFlagService,
    private fullStoryService: FullstoryService
  ) {
    this.setupNavBarItems();
  }

  ngOnInit(): void {
    this.showOutlet = true;

    this.userApi.currentUser$
      .pipe(
        filter((user): user is User => !!user),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe((user) => {
        this.fullStoryService.identifyUser(`${user.id}`, {
          release: `${window.location.hostname}@${ConfigConstant.VERSION}`,
          displayName: user.name,
          email: user.email,
          mobileNumber: user.mobileNumber,
          userRole: user?.roles?.length ? user.roles.map(role => role.userRole).join(' , ') : 'undefined',
          loginDate: new Date().toISOString()
        });

        if (!this.router.url.split('/').splice(1).join('/').split('?')[0].split('/')[0]) {
          this.router.navigateByUrl(this.userRoleService.landingRoute());
        }
      });

    this.languageService.languageChanged.pipe(takeUntil(this.destroy$)).subscribe((_) => {
      this.showOutlet = false;
      setTimeout(() => {
        this.showOutlet = true;
      }, 100);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private setupNavBarItems() {
    combineLatest([
      this.translate.stream(['NavigationBar']),
      this.featureFlagService.flags$,
      this.userApi.currentUser$.pipe(filter((user): user is User => !!user)),
    ])
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(([translations, , currentUser]) => {
        this.currentUser = currentUser;
        const { customDashboardUrl, dashboardsInfo, customerType, fleetType } = this.currentUser;
        const isSass = customerType === CustomerServiceType.SAAS;
        const isBiddingCustomer = this.userRoleService.hasBiddingTemplate();
        const defaultDashboardPage = {
          key: RoutesConstants.NAVIGATION_KEY_DASHBOARD,
          pageTitle: translations.NavigationBar.Dashboard,
          title: translations.NavigationBar.Dashboard,
          path: this.userRoleService.isUserHasRoles(RolesConstants.DASHBOARD_ORGANIZATION_VIEW)
            ? RoutesUtil.OrganizationDashboard.url()
            : RoutesUtil.Dashboard.url(),
          isSelected: false,
          isVisible: this.userRoleService.isUserHasRoles(RolesConstants.DASHBOARD_ANY_VIEW),
          icon: 'outline-home',
        };

        const dashboardGroup = {
          key: RoutesConstants.NAVIGATION_KEY_DASHBOARDS,
          pageTitle: translations.NavigationBar.Dashboards,
          title: translations.NavigationBar.Dashboards,
          path: RoutesUtil.Dashboards.url(),
          isSelected: false,
          isVisible: this.userRoleService.isUserHasRoles(RolesConstants.DASHBOARD_ANY_VIEW),
          icon: 'outline-home',
          showSubMenu: false,
          subMenu: [],
        };

        const hasDashboardGroup = !!dashboardsInfo?.length;
        const dashboardItems = hasDashboardGroup ? [dashboardGroup] : [defaultDashboardPage];

        if (hasDashboardGroup) {
          dashboardGroup.subMenu.push(defaultDashboardPage);
          dashboardsInfo.forEach((dashboard, i) => {
            dashboardGroup.subMenu.push({
              key: RoutesConstants.NAVIGATION_KEY_MY_DASHBOARD,
              pageTitle: translations.NavigationBar.MyDashboard,
              title: dashboard.name ? this.translate.instant(dashboard.name) : translations.NavigationBar.MyDashboard,
              path: RoutesUtil.CustomDashboard.url({ name: dashboard.name }),
              isSelected: false,
              isVisible: this.userRoleService.isUserHasRoles(RolesConstants.DASHBOARD_ANY_VIEW),
              icon: 'outline-home',
            });
          });
        }

        this.navigationItems = [
          ...dashboardItems,
          {
            key: RoutesConstants.NAVIGATION_KEY_DASHBOARD,
            pageTitle: translations.NavigationBar.BusinessUnits,
            title: translations.NavigationBar.BusinessUnits,
            path: RoutesUtil.Customers.url(),
            isSelected: false,
            isVisible: this.userRoleService.isUserHasRoles(RolesConstants.CUSTOMER_LIST_VIEW),
            icon: 'outline-profile-two-users',
          },
          {
            key: RoutesConstants.NAVIGATION_KEY_SHIPMENTS,
            pageTitle: translations.NavigationBar.Shipments,
            title: translations.NavigationBar.Shipments,
            path: RoutesUtil.ShipmentsList.url({ type: ShipmentsListType.All }),
            isSelected: false,
            isVisible: this.userRoleService.isUserHasRoles(RolesConstants.SHIPMENT_LIST_VIEW),
            icon: 'outline-box',
          },
          {
            key: RoutesConstants.NAVIGATION_KEY_SHIPMENTS,
            pageTitle: translations.NavigationBar.Shipments,
            title: translations.NavigationBar.Shipments,
            path: RoutesUtil.ShipmentsList.url({ type: ShipmentsListType.All }),
            isSelected: false,
            isVisible: this.userRoleService.isUserHasRoles(RolesConstants.CUSTOMER_SHIPMENT_LIST_VIEW) && isBiddingCustomer,
            icon: 'outline-box',
          },
          {
            key: RoutesConstants.NAVIGATION_KEY_SHIPMENTS,
            pageTitle: translations.NavigationBar.Shipments,
            title: translations.NavigationBar.Shipments,
            path: RoutesUtil.ShipmentsList.url({ type: ShipmentsListType.All }),
            isSelected: false,
            isVisible: this.userRoleService.isUserHasRoles(RolesConstants.CUSTOMER_SHIPMENT_LIST_VIEW) && !isBiddingCustomer,
            icon: 'outline-box'
          },
          {
            key: RoutesConstants.NAVIGATION_KEY_ORDERS,
            pageTitle: translations.NavigationBar.Orders,
            title: translations.NavigationBar.Orders,
            path: RoutesUtil.Orders.url(),
            isSelected: false,
            isVisible: this.userRoleService.isUserHasRoles(RolesConstants.ORDERS) && !isBiddingCustomer,
            icon: 'receipt-item',
          },
          {
            key: RoutesConstants.NAVIGATION_KEY_TRIP_PLANNING,
            pageTitle: translations.NavigationBar.TripPlanning,
            title: translations.NavigationBar.TripPlanning,
            path: RoutesUtil.TripPlanning.url(),
            isSelected: false,
            isVisible: this.userRoleService.isUserHasRoles(RolesConstants.FLEET_USER_TRUCKS),
            icon: 'fleet',
          },
          {
            key: RoutesConstants.NAVIGATION_KEY_TRIP_PLANNING,
            pageTitle: translations.NavigationBar.TripPlanning,
            title: translations.NavigationBar.TripPlanning,
            path: RoutesUtil.TripPlanningFleets.url(),
            isSelected: false,
            isVisible: this.userRoleService.isUserHasRoles(RolesConstants.CUSTOMER_TRIP_PLANNING) && isSass,
            icon: 'fleet',
          },
          {
            key: RoutesConstants.NAVIGATION_KEY_FLEET_PROVIDERS,
            pageTitle: translations.NavigationBar.FleetProviders,
            title: translations.NavigationBar.FleetProviders,
            path: RoutesUtil.Fleets.url(),
            isSelected: false,
            isVisible: this.userRoleService.isUserHasRoles(RolesConstants.FLEET_LIST_VIEW) && !isBiddingCustomer,
            icon: 'outline-truck',
          },
          {
            key: RoutesConstants.NAVIGATION_KEY_LIVE_TRACKING,
            pageTitle: translations.NavigationBar.LiveTracking,
            title: translations.NavigationBar.LiveTracking,
            path: RoutesUtil.LiveTracking.url(),
            isSelected: false,
            isVisible: true,
            icon: 'outline-location',
          },
          {
            key: RoutesConstants.NAVIGATION_KEY_FLEET_INVOICES,
            pageTitle: translations.NavigationBar.Invoices,
            title: translations.NavigationBar.Invoices,
            path: RoutesUtil.InvoicesFleet.url(),
            isSelected: false,
            isVisible:
              this.userRoleService.isUserHasRoles(RolesConstants.VIEW_FLEET_INVOICES_LIST) &&
              this.featureFlagService.isEnabled(FeatureFlagConstant.FleetInvoices),
            icon: 'outline-wallet',
          },
          {
            key: RoutesConstants.NAVIGATION_KEY_INVOICES,
            pageTitle: translations.NavigationBar.Invoices,
            title: translations.NavigationBar.Invoices,
            path: RoutesUtil.CustomerInvoices.url(),
            isSelected: false,
            isVisible:
              this.userRoleService.isUserHasRoles(RolesConstants.INVOICE_LIST_VIEW) &&
              this.currentUser.customerType === CustomerServiceType.Service,
            icon: 'outline-wallet',
          },
          {
            key: RoutesConstants.NAVIGATION_KEY_TRANSACTIONS,
            pageTitle: translations.NavigationBar.Transactions,
            title: translations.NavigationBar.Transactions,
            path: RoutesUtil.Transactions.url(),
            isSelected: false,
            isVisible:
              this.currentUser.customerLabel === CompanyLabelType.Ycc || this.currentUser.customerLabel === CompanyLabelType.YccCustomer,
            icon: 'bulk-receipt-item',
          },
          {
            key: RoutesConstants.NAVIGATION_KEY_TRANSPORTATION_PAYMENT,
            pageTitle: translations.NavigationBar.TransportationPayments,
            title: translations.NavigationBar.TransportationPayments,
            path: RoutesUtil.TransportationPayments.url(),
            isSelected: false,
            isVisible:
              this.userRoleService.isUserHasRoles(RolesConstants.TRANSPORTATION_PAYMENTS_VIEW) &&
              this.currentUser.fleetType === FleetType.Global,
            icon: 'transportation-payments',
          },
          {
            key: RoutesConstants.NAVIGATION_KEY_REPORTS,
            pageTitle: translations.NavigationBar.Reports,
            title: translations.NavigationBar.Reports,
            path: RoutesUtil.Reports.url(),
            isSelected: true,
            isVisible: this.userRoleService.isUserHasRoles(RolesConstants.REPORT) && !isBiddingCustomer,
            icon: 'bold-diagram',
            showSubMenu: false,
            subMenu: [
              {
                key: RoutesConstants.NAVIGATION_KEY_TRUCKS_REPORTS,
                pageTitle: translations.NavigationBar.SubMenu.TrucksReport,
                title: translations.NavigationBar.SubMenu.TrucksReport,
                path: RoutesUtil.ReportsTrucks.url(),
                isSelected: false,
                isVisible: this.userRoleService.isUserHasRoles(RolesConstants.TRUCKS_REPORT),
              },
              {
                key: RoutesConstants.NAVIGATION_KEY_G2G_REPORT,
                pageTitle: translations.NavigationBar.SubMenu.G2G,
                title: translations.NavigationBar.SubMenu.G2G,
                path: RoutesUtil.ReportsG2G.url(),
                isSelected: false,
                isVisible: this.userRoleService.isUserHasRoles(RolesConstants.G2G_REPORT),
              },
              {
                key: RoutesConstants.NAVIGATION_KEY_G2G_INTEGRATION,
                pageTitle: translations.NavigationBar.SubMenu.G2GIntegration,
                title: translations.NavigationBar.SubMenu.G2GIntegration,
                path: RoutesUtil.ReportsG2gIntegration.url(),
                isSelected: false,
                isVisible: this.userRoleService.isUserHasRoles(RolesConstants.G2G_REPORT),
              },
              {
                key: RoutesConstants.NAVIGATION_KEY_TRUCK_UTILIZATION_REPORT,
                pageTitle: translations.NavigationBar.TruckUtilization,
                title: translations.NavigationBar.TruckUtilization,
                path: RoutesUtil.ReportsTruckUtilization.url(),
                isSelected: false,
                isVisible: this.userRoleService.isUserHasRoles(RolesConstants.TRUCK_UTILIZATION_REPORT),
              },
            ],
          },
          {
            key: RoutesConstants.NAVIGATION_KEY_MANAGE,
            pageTitle: translations.NavigationBar.Manage,
            title: translations.NavigationBar.Manage,
            path: RoutesConstants.MANAGE_PATH,
            isSelected: false,
            isVisible: this.userRoleService.isUserHasRoles(RolesConstants.MANAGE_VIEW),
            icon: 'outline-setting',
            showSubMenu: false,
            subMenu: [
              {
                key: RoutesConstants.CUSTOMER_INFO_PATH,
                pageTitle: translations.NavigationBar.SubMenu.CompanyInfo,
                title: translations.NavigationBar.SubMenu.CompanyInfo,
                path: RoutesUtil.CustomerInfo.url(),
                isSelected: false,
                isVisible: this.userRoleService.isUserHasRoles(RolesConstants.CUSTOMER_INFO_VIEW),
              },
              {
                key: RoutesConstants.NAVIGATION_KEY_PROFILE,
                pageTitle: translations.NavigationBar.SubMenu.Profile,
                title: translations.NavigationBar.SubMenu.Profile,
                path: RoutesUtil.CustomersProfile.url(),
                isSelected: false,
                isVisible: this.userRoleService.isUserHasRoles(RolesConstants.PROFILE_VIEW),
              },
              {
                key: RoutesConstants.NAVIGATION_KEY_MANAGE_USERS,
                pageTitle: translations.NavigationBar.SubMenu.Users,
                title: translations.NavigationBar.SubMenu.Users,
                path: RoutesUtil.Users.url(),
                isSelected: false,
                isVisible: this.userRoleService.isUserHasRoles(RolesConstants.USERS_VIEW),
              },
              {
                key: RoutesConstants.NAVIGATION_KEY_MANAGE_MY_CUSTOMERS,
                pageTitle: translations.NavigationBar.SubMenu.MyCustomers,
                title: translations.NavigationBar.SubMenu.MyCustomers,
                path: RoutesUtil.MyCustomers.url(),
                isSelected: false,
                isVisible: this.userRoleService.isUserHasRoles(RolesConstants.MY_CUSTOMERS_VIEW),
                activePaths: [RoutesUtil.MyCustomersAdd.path, RoutesUtil.CustomersVerify.path],
              },
              {
                key: RoutesConstants.MY_WALLET_PATH,
                pageTitle: translations.NavigationBar.SubMenu.MyWallet,
                title: translations.NavigationBar.SubMenu.MyWallet,
                path: RoutesUtil.MyWalletView.url(),
                isSelected: false,
                isVisible: this.userRoleService.isUserHasRoles(RolesConstants.MY_WALLET_VIEW) && this.currentUser.isWalletEnabled,
              },
              {
                key: RoutesConstants.NAVIGATION_KEY_MANAGE_RECEIVERS,
                pageTitle: translations.NavigationBar.SubMenu.Receivers,
                title: translations.NavigationBar.SubMenu.Receivers,
                path: RoutesConstants.RECEIVERS_PATH,
                isSelected: false,
                isVisible: this.userRoleService.isUserHasRoles(RolesConstants.RECEIVERS_VIEW),
                activePaths: [RoutesUtil.ReceiversAdd.path],
              },
              {
                key: RoutesConstants.NAVIGATION_KEY_CUSTOMER_MADAR_CONTRACTS,
                pageTitle: translations.NavigationBar.SubMenu.MadarContracts,
                title: translations.NavigationBar.SubMenu.MadarContracts,
                path: RoutesUtil.ContractsOpsList.url(),
                isSelected: false,
                isVisible: customerType === CustomerServiceType.Service || fleetType === FleetType.Global,
                //   this.userRoleService.isUserHasRoles(RolesConstants.CONTRACTS_CUSTOMER_VIEW)
              },

              // {
              //   key: RoutesConstants.NAVIGATION_KEY_FLEET_MADAR_CONTRACTS,
              //   pageTitle: translations.NavigationBar.SubMenu.MadarContracts,
              //   title: translations.NavigationBar.SubMenu.MadarContracts,
              //   path: RoutesUtil.FleetMadarContracts.url(),
              //   isSelected: false,
              //   isVisible:
              //     (this.userRoleService.isUserHasRoles(RolesConstants.CONTRACTS_FLEET_VIEW) &&
              //       this.currentUser.fleetType === FleetType.Global),
              // },
              {
                key: RoutesConstants.NAVIGATION_KEY_FLEET_USER_TRUCKS,
                pageTitle: translations.NavigationBar.FleetUserCompanyInfo,
                title: translations.NavigationBar.FleetUserCompanyInfo,
                path: RoutesUtil.FleetInfo.url(),
                isSelected: false,
                isVisible: this.userRoleService.isUserHasRoles(RolesConstants.FLEET_INFO_VIEW),
              },
              {
                key: RoutesConstants.NAVIGATION_KEY_FLEET_USER_TRUCKS,
                pageTitle: translations.NavigationBar.FleetUserTrucks,
                title: translations.NavigationBar.FleetUserTrucks,
                path: RoutesUtil.FleetsTrucks.url(),
                isSelected: false,
                isVisible: this.userRoleService.isUserHasRoles(RolesConstants.FLEET_USER_TRUCKS),
              },

              {
                key: RoutesConstants.NAVIGATION_KEY_FLEET_USER_RENTED_TRUCKS,
                pageTitle: translations.NavigationBar.FleetUserRentedTrucks,
                title: translations.NavigationBar.FleetUserRentedTrucks,
                path: RoutesUtil.FleetsRentedTrucks.url(),
                isSelected: false,
                isVisible: this.userRoleService.isUserHasRoles(RolesConstants.FLEET_USER_RENTED_TRUCKS),
              },
              {
                key: RoutesConstants.NAVIGATION_KEY_FLEET_USER_DRIVERS,
                pageTitle: translations.NavigationBar.FleetUserDrivers,
                title: translations.NavigationBar.FleetUserDrivers,
                path: RoutesUtil.FleetsDrivers.url(),
                isSelected: false,
                isVisible: this.userRoleService.isUserHasRoles(RolesConstants.FLEET_USER_DRIVERS),
              },
              {
                key: RoutesConstants.NAVIGATION_KEY_FLEET_USER_USERS,
                pageTitle: translations.NavigationBar.FleetUserUsers,
                title: translations.NavigationBar.FleetUserUsers,
                path: RoutesUtil.FleetsUsers.url(),
                isSelected: false,
                isVisible: this.userRoleService.isUserHasRoles(RolesConstants.FLEET_USER_USERS),
              },
              {
                key: RoutesConstants.NAVIGATION_KEY_CUSTOMER_FLEET_CONTRACTS,
                pageTitle:
                  this.userRoleService.isUserHasRoles(RolesConstants.CONTRACTS_FLEET_VIEW) &&
                  this.currentUser.fleetType === FleetType.Specific
                    ? translations.NavigationBar.SubMenu.CustomerContracts
                    : translations.NavigationBar.SubMenu.CustomerFleetContracts,
                title:
                  this.userRoleService.isUserHasRoles(RolesConstants.CONTRACTS_FLEET_VIEW) &&
                  this.currentUser.fleetType === FleetType.Specific
                    ? translations.NavigationBar.SubMenu.CustomerContracts
                    : translations.NavigationBar.SubMenu.CustomerFleetContracts,
                path: RoutesUtil.ContractsSaasList.url(),
                isSelected: false,
                isVisible:
                  (customerType === CustomerServiceType.SAAS &&
                    this.userRoleService.isUserHasRoles(RolesConstants.CONTRACTS_CUSTOMER_VIEW)) ||
                  (this.userRoleService.isUserHasRoles(RolesConstants.CONTRACTS_FLEET_VIEW) && fleetType === FleetType.Specific),
              },
            ],
          },
            {
            key: RoutesConstants.NAVIGATION_KEY_LOGS,
            pageTitle: translations.NavigationBar.Logs,
            title: translations.NavigationBar.Logs,
            path: RoutesConstants.LOGS_PATH,
            isSelected: false,
            isVisible: this.userRoleService.isUserHasRoles(RolesConstants.WCC_LOGS_VIEW) && this.currentUser.creationTemplates?.some(template => template.type === ShipmentCreationTemplateTypes.Orders),
            icon: 'logs',
            showSubMenu: false,
            subMenu: [
              {
                key: RoutesConstants.NAVIGATION_KEY_LOGS_WCC,
                pageTitle: translations.NavigationBar.SubMenu.ordersLogs,
                title: translations.NavigationBar.SubMenu.ordersLogs,
                path: RoutesUtil.LogsWcc.url(),
                isSelected: false,
                isVisible: this.userRoleService.isUserHasRoles(RolesConstants.WCC_LOGS_VIEW) && this.currentUser.creationTemplates?.some(template => template.type === ShipmentCreationTemplateTypes.Orders),
              },
            ],
          },
        ];
      });
  }
}
